import { useState, useEffect } from 'react';
import { GOLD, EMBER, FAINT, GHOST, MONO } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Label, Ic } from './shared';

const API = process.env.REACT_APP_BACKEND_URL;

export function SpendingAnalysisScreen({ user, debts, navigate }) {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/insights/history`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.analyses?.length) setLatest(data.analyses[0]);
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  const gradeColor = latest?.spending_grade
    ? { A: '#22c55e', B: '#84cc16', C: '#eab308', D: '#f97316', F: '#ef4444' }[latest.spending_grade] || 'var(--mute)'
    : 'var(--mute)';

  const now = new Date();
  const monthLabel = now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase();

  if (loading) return (
    <div data-testid="spending-analysis-screen" style={{ padding: '60px 28px', textAlign: 'center' }}>
      <div className="pulse-a">{Ic.chart(32, GOLD)}</div>
    </div>
  );

  return (
    <div data-testid="spending-analysis-screen" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <Label>SPENDING ANALYSIS</Label>

        {/* Monthly Summary */}
        <div style={{ padding: 16, background: 'var(--surface)', border: `1px solid ${FAINT}`, marginBottom: 16 }}>
          <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>{monthLabel} SUMMARY</Mo>
          {latest ? (
            <>
              <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <Dp style={{ fontSize: 24, fontWeight: 800, color: EMBER, display: 'block' }}>
                    ${(latest.total_spending || 0).toLocaleString()}
                  </Dp>
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1 }}>SPENT</Mo>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <Dp style={{ fontSize: 24, fontWeight: 800, color: '#22c55e', display: 'block' }}>
                    ${(latest.total_income || 0).toLocaleString()}
                  </Dp>
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1 }}>INCOME</Mo>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1, padding: 8, background: 'var(--faint)', borderRadius: 8, textAlign: 'center' }}>
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block' }}>GRADE</Mo>
                  <Dp style={{ fontSize: 20, fontWeight: 800, color: gradeColor }}>{latest.spending_grade || '—'}</Dp>
                </div>
                <div style={{ flex: 1, padding: 8, background: 'var(--faint)', borderRadius: 8, textAlign: 'center' }}>
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block' }}>TRANSACTIONS</Mo>
                  <Dp style={{ fontSize: 20, fontWeight: 800, color: 'var(--light)' }}>{latest.transaction_count || 0}</Dp>
                </div>
                <div style={{ flex: 1, padding: 8, background: 'var(--faint)', borderRadius: 8, textAlign: 'center' }}>
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block' }}>% OF INCOME</Mo>
                  <Dp style={{ fontSize: 20, fontWeight: 800, color: latest.income_pct > 85 ? EMBER : GOLD }}>{latest.income_pct || 0}%</Dp>
                </div>
              </div>
              <Mo style={{ fontSize: 10, color: GHOST, display: 'block', marginTop: 8, textAlign: 'right' }}>
                Last analysis: {(() => { const dt = new Date(latest.created_at); return `${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getDate()).padStart(2,'0')}/${dt.getFullYear()}`; })()}
              </Mo>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Dp style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 6 }}>No data yet</Dp>
              <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.6 }}>
                Run a Deep Spending Analysis from the Insights tab to see your spending data here.
              </Bd>
            </div>
          )}
        </div>

        {/* CTA to Insights */}
        <Btn data-testid="go-to-insights-btn" onClick={() => navigate && navigate('insights')} gold>
          {latest ? 'VIEW FULL INSIGHTS' : 'RUN SPENDING ANALYSIS'}
        </Btn>

        <Mo style={{ fontSize: 10, color: GHOST, display: 'block', textAlign: 'center', marginTop: 12, lineHeight: 1.5 }}>
          Deep Spending Analysis lives in the Insights tab. This page shows a summary of your latest analysis.
        </Mo>
      </div>
    </div>
  );
}
